/**
 * Announcements — migrated from mockData to live svdy-platform API.
 *
 * Backend shape: AnnouncementResponse = { id, title, content, priority,
 * status, created_at, created_by, published_at }. Much simpler than the
 * mock (no category / scope / views / isPinned / author avatar) — we
 * lean on priority + status. Content is plain multiline text (no rich
 * text editor; per Wave 3.1 § 11 "Don't build a rich text editor").
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import AudienceScopeField from '../../../components/organization/AudienceScopeField';
import { activeLocale } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';

type Announcement = Schema<'AnnouncementResponse'>;
type AnnouncementCreate = Schema<'AnnouncementCreate'>;
type AnnouncementUpdate = Schema<'AnnouncementUpdate'>;

type FormState = Partial<Announcement>;

const PRIORITIES = [
  { value: 'low', color: 'default' as const },
  { value: 'normal', color: 'info' as const },
  { value: 'high', color: 'warning' as const },
  { value: 'urgent', color: 'error' as const },
];

const STATUSES = [
  { value: 'draft', color: 'default' as const },
  { value: 'published', color: 'success' as const },
  { value: 'archived', color: 'secondary' as const },
];

function priorityColor(
  p: string | null | undefined,
): 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' {
  return PRIORITIES.find((x) => x.value === p)?.color ?? 'default';
}

function statusColor(
  s: string | null | undefined,
): 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' {
  return STATUSES.find((x) => x.value === s)?.color ?? 'default';
}

const Announcements: React.FC = () => {
  const { t } = useTranslation();

  // Translated label getters (defined inside component so t() is in scope).
  const priorityLabel = (p: string | null | undefined): string =>
    p ? t(`org:announcements.priority.${p}`, p) : t('org:announcements.priority.normal');
  const statusLabel = (s: string | null | undefined): string =>
    s ? t(`org:announcements.status.${s}`, s) : t('org:announcements.status.draft');

  const [rows, setRows] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [current, setCurrent] = useState<FormState | null>(null);
  const [toDelete, setToDelete] = useState<Announcement | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationApi.announcementsApi.list();
      setRows(data);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:announcements.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // BUG-3 fix (2026-07-07): deep-link support — /organization/announcements?id=<id>
  // opens that announcement's detail dialog. Used by the notification bell
  // click-through (org/service.py fanout_to_org_workers). Mirrors the
  // SystemSupport.tsx ?case_id= pattern. A ref latch avoids re-opening the
  // same id on every rows refetch while still reacting to a genuinely new id.
  const [searchParams, setSearchParams] = useSearchParams();
  const openedIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    const fromUrl = searchParams.get('id');
    if (!fromUrl || openedIdRef.current === fromUrl || rows.length === 0) return;
    const row = rows.find((a) => a.id === fromUrl);
    if (row) {
      openedIdRef.current = fromUrl;
      handleOpenEditDialog(row);
      // Strip the query param so a dialog close + refresh doesn't reopen it.
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, rows, setSearchParams]);

  const summaryStats = useMemo(() => {
    const total = rows.length;
    const published = rows.filter((a) => a.status === 'published').length;
    const draft = rows.filter((a) => a.status === 'draft' || !a.status).length;
    const urgent = rows.filter((a) => a.priority === 'urgent').length;
    const archived = rows.filter((a) => a.status === 'archived').length;
    return { total, published, draft, urgent, archived };
  }, [rows]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:announcements.stats.total'),
        description: t('org:announcements.stats.totalDescription'),
        avatar: {
          icon: <CampaignOutlinedIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'published',
        value: summaryStats.published,
        title: t('org:announcements.stats.published'),
        description: t('org:announcements.stats.publishedDescription'),
        avatar: {
          icon: <AnnouncementIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'draft',
        value: summaryStats.draft,
        title: t('org:announcements.stats.draft'),
        description: t('org:announcements.stats.draftDescription'),
        avatar: {
          icon: <DraftsOutlinedIcon />,
          bgcolor: 'grey.200',
          color: 'text.secondary',
        },
      },
      {
        id: 'urgent',
        value: summaryStats.urgent,
        title: t('org:announcements.stats.urgent'),
        description: t('org:announcements.stats.urgentDescription'),
        avatar: {
          icon: <PriorityHighIcon />,
          bgcolor: 'error.light',
          color: 'error.main',
        },
      },
    ],
    [summaryStats, t],
  );

  const filtered = useMemo(() => {
    const scoped = rows.filter((a) => {
      if (priorityFilter !== 'all' && (a.priority ?? '') !== priorityFilter) return false;
      if (statusFilter !== 'all' && (a.status ?? '') !== statusFilter) return false;
      return true;
    });
    const needle = filterText.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter(
      (a) =>
        a.title.toLowerCase().includes(needle) ||
        a.content.toLowerCase().includes(needle),
    );
  }, [rows, filterText, priorityFilter, statusFilter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // -------------------------------------------------------------------------
  // CRUD
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!current) return false;
    const errs: Record<string, string> = {};
    if (!current.title?.trim()) errs.title = t('org:announcements.fields.titleRequired');
    if (!current.content?.trim()) errs.content = t('org:announcements.fields.contentRequired');
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    setCurrent({ priority: 'normal', status: 'draft' });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (row: Announcement) => {
    setCurrent({ ...row });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrent(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    if (!validateForm() || !current) return;
    setSaving(true);
    try {
      if (current.id) {
        const update: AnnouncementUpdate = {
          title: current.title ?? null,
          content: current.content ?? null,
          priority: current.priority ?? null,
          status: current.status ?? null,
          audience_scope: current.audience_scope ?? 'all',
          audience_department_ids: current.audience_department_ids ?? [],
        };
        await organizationApi.announcementsApi.update(current.id, update);
        setToast(t('org:announcements.toast.updated', { name: current.title }));
      } else {
        const create: AnnouncementCreate = {
          title: current.title!,
          content: current.content!,
          priority: current.priority ?? 'normal',
          status: current.status ?? 'draft',
          audience_scope: current.audience_scope ?? 'all',
          audience_department_ids: current.audience_department_ids ?? [],
        };
        await organizationApi.announcementsApi.create(create);
        setToast(t('org:announcements.toast.created', { name: create.title }));
      }
      handleCloseFormDialog();
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:announcements.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (row: Announcement) => {
    setToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await organizationApi.announcementsApi.delete(toDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== toDelete.id));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      setToast(t('org:announcements.toast.deleted', { name: toDelete.title }));
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:announcements.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:announcements.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.announcementsApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:announcements.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:announcements.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  const handleArchiveBulk = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:announcements.toast.selectOneArchive'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.announcementsApi.update(id, {
          status: 'archived',
        });
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:announcements.toast.bulkArchived', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:announcements.toast.bulkArchivedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value } = event.target;
    setCurrent((prev) => (prev ? { ...prev, [name]: value } : null));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrent((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  // -------------------------------------------------------------------------
  // Row selection / pagination
  // -------------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((r) => r.id) : []);
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:announcements.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:announcements.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:announcements.addButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleArchiveBulk}
            startIcon={<ArchiveOutlinedIcon />}
            size="small"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:announcements.archiveButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('org:announcements.filters.priorityLabel')}</InputLabel>
          <Select
            value={priorityFilter}
            label={t('org:announcements.filters.priorityLabel')}
            onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:announcements.filters.allPriorities')}</MenuItem>
            {PRIORITIES.map((p) => (
              <MenuItem key={p.value} value={p.value}>{priorityLabel(p.value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('org:announcements.filters.statusLabel')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('org:announcements.filters.statusLabel')}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:announcements.filters.allStatuses')}</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{statusLabel(s.value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          label={t('org:announcements.searchLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 320, maxWidth: '100%' }}
        />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Chips §5 — active non-default filters, individually clearable. */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (priorityFilter !== 'all') {
          chips.push({
            key: 'pri',
            label: t('org:announcements.filters.priorityChip', { label: priorityLabel(priorityFilter) }),
            onClear: () => { setPriorityFilter('all'); setPage(0); },
          });
        }
        if (statusFilter !== 'all') {
          chips.push({
            key: 'status',
            label: t('org:announcements.filters.statusChip', { label: statusLabel(statusFilter) }),
            onClear: () => { setStatusFilter('all'); setPage(0); },
          });
        }
        if (filterText.trim()) {
          chips.push({
            key: 'search',
            label: t('org:announcements.filters.searchChip', { query: filterText.trim() }),
            onClear: () => { setFilterText(''); setPage(0); },
          });
        }
        if (chips.length === 0) return null;
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {chips.map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                onDelete={c.onClear}
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setPriorityFilter('all');
                setStatusFilter('all');
                setFilterText('');
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'text.secondary',
                minWidth: 0,
                px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}
            >
              {t('org:announcements.filters.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText || priorityFilter !== 'all' || statusFilter !== 'all'
              ? t('org:announcements.empty.noMatches')
              : t('org:announcements.empty.noResults')}
          </Typography>
          {!filterText && priorityFilter === 'all' && statusFilter === 'all' && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:announcements.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:announcements.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:announcements.table.title')}</TableCell>
                <TableCell>{t('org:announcements.table.priority')}</TableCell>
                <TableCell>{t('org:announcements.table.status')}</TableCell>
                <TableCell>{t('org:announcements.table.published')}</TableCell>
                <TableCell>{t('org:announcements.table.created')}</TableCell>
                <TableCell align="center">{t('org:announcements.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((a) => {
                const selected = isSelected(a.id);
                return (
                  <TableRow
                    hover
                    key={a.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, a.id)}
                        inputProps={{ 'aria-label': a.title }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {a.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {a.content}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={priorityLabel(a.priority)}
                        color={priorityColor(a.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel(a.status)}
                        color={statusColor(a.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {a.published_at
                        ? new Date(a.published_at).toLocaleDateString(activeLocale())
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {a.created_at
                        ? new Date(a.created_at).toLocaleDateString(activeLocale())
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label={t('org:shared.table.edit')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(a);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('common:actions.delete')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(a);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, py: 2 }}>
          {current?.id ? t('org:announcements.editDialogTitle') : t('org:announcements.addDialogTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label={t('org:announcements.fields.title')}
            fullWidth
            variant="outlined"
            value={current?.title || ''}
            onChange={handleFormChange}
            required
            error={!!formErrors.title}
            helperText={formErrors.title}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="content"
            label={t('org:announcements.fields.content')}
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            placeholder={t('org:announcements.fields.contentPlaceholder')}
            value={current?.content || ''}
            onChange={handleFormChange}
            required
            error={!!formErrors.content}
            helperText={formErrors.content}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="priority-label">{t('org:announcements.fields.priority')}</InputLabel>
                <Select
                  labelId="priority-label"
                  name="priority"
                  value={current?.priority ?? 'normal'}
                  label={t('org:announcements.fields.priority')}
                  onChange={handleSelectChange}
                >
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {priorityLabel(p.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="status-label">{t('org:announcements.fields.status')}</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={current?.status ?? 'draft'}
                  label={t('org:announcements.fields.status')}
                  onChange={handleSelectChange}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {statusLabel(s.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <AudienceScopeField
                scope={current?.audience_scope ?? 'all'}
                departmentIds={current?.audience_department_ids ?? []}
                onChange={(next) => setCurrent((prev) => ({ ...prev, ...next }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : current?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:announcements.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — shared ConfirmDialog primitive. */}
      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:announcements.deleteTitle')}
        description={t('org:announcements.deleteDescription')}
        subject={
          toDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {toDelete.title}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Announcements;
